import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Ban, FileMinus, IndianRupee, Printer } from "lucide-react";
import { amountInWords, api, errMsg, inr } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { Money, StatusBadge, TableSkeleton, fmtDate } from "../components/erp";
import { Button } from "../components/ui/button";
import { addressText } from "../components/AddressFields";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { Separator } from "../components/ui/separator";

export default function InvoiceDetail() {
  const { id } = useParams();
  const { activeId } = useCompany();
  const [inv, setInv] = useState(null);
  const [cnOpen, setCnOpen] = useState(false);
  const [cnReason, setCnReason] = useState("");
  const [cnQtys, setCnQtys] = useState({});
  const [cnBusy, setCnBusy] = useState(false);
  const nav = useNavigate();

  const load = () => api.get(`/invoices/${id}`).then((r) => setInv(r.data)).catch((e) => { toast.error(errMsg(e)); nav("/invoices"); });
  useEffect(() => { if (activeId) { setInv(null); load(); } }, [id, activeId]); // eslint-disable-line

  if (!inv) return <TableSkeleton />;
  const c = inv.company || {};
  const p = inv.party || {};

  const createCreditNote = async () => {
    const items = inv.items
      .filter((l) => (cnQtys[l.name] || 0) > 0)
      .map((l) => ({ item_id: l.item_id, name: l.name, hsn: l.hsn, unit: l.unit,
                     qty: +cnQtys[l.name], rate: l.rate, tax_rate: l.tax_rate }));
    if (items.length === 0) { toast.error("Enter return quantity for at least one item"); return; }
    if (!cnReason.trim()) { toast.error("Reason is required"); return; }
    setCnBusy(true);
    try {
      const { data } = await api.post("/credit-notes", {
        invoice_id: id, date: new Date().toISOString().slice(0, 10), reason: cnReason, items });
      toast.success(`Credit note ${data.doc_no} posted — stock returned, balance reduced`);
      setCnOpen(false); setCnReason(""); setCnQtys({});
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setCnBusy(false); }
  };

  const cancel = async () => {
    try {
      await api.post(`/invoices/${id}/cancel`);
      toast.success("Invoice cancelled — stock and ledger reversed");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="invoice-detail-page" className="max-w-4xl">
      <div className="no-print mb-4 flex flex-wrap items-center justify-between gap-2">
        <Button variant="ghost" size="sm" className="-ml-2" onClick={() => nav("/invoices")} data-testid="back-to-invoices">
          <ArrowLeft className="mr-1 h-4 w-4" /> Invoices
        </Button>
        <div className="flex gap-2">
          {inv.status === "posted" && (
            <Button variant="outline" data-testid="create-credit-note-btn" onClick={() => setCnOpen(true)}>
              <FileMinus className="mr-1.5 h-4 w-4" /> Credit Note
            </Button>
          )}
          {inv.status === "posted" && inv.balance > 0 && (
            <Button variant="outline" data-testid="record-payment-btn" onClick={() => nav(`/payments?new=1&party=${inv.party_id}`)}>
              <IndianRupee className="mr-1.5 h-4 w-4" /> Record Payment
            </Button>
          )}
          {inv.status !== "cancelled" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" data-testid="cancel-invoice-btn" className="text-red-600 border-red-200 dark:border-red-900">
                  <Ban className="mr-1.5 h-4 w-4" /> Cancel
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent aria-describedby={undefined} className="bg-white dark:bg-slate-900">
                <AlertDialogHeader>
                  <AlertDialogTitle>Cancel invoice {inv.invoice_no}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This voids the invoice, returns item stock and reverses the ledger entry. The record is kept for audit — it is never deleted.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="cancel-no">Keep invoice</AlertDialogCancel>
                  <AlertDialogAction data-testid="cancel-yes" onClick={cancel} className="bg-red-600 hover:bg-red-700">Cancel invoice</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <Button data-testid="print-invoice-btn" onClick={() => window.print()}
            className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900">
            <Printer className="mr-1.5 h-4 w-4" /> Print / PDF
          </Button>
        </div>
      </div>

      <div className="print-area rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 sm:p-8" data-testid="invoice-preview">
        <div className="flex flex-wrap justify-between gap-4">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg text-white font-display font-extrabold"
                style={{ background: c.accent || "#0F172A" }}>{(c.code || "KG").slice(0, 2)}</span>
              <div>
                <h1 className="font-display text-lg font-bold leading-tight">{c.name}</h1>
                <p className="text-xs text-slate-500">{[c.address, c.city, c.state, c.pin].filter(Boolean).join(", ")}</p>
              </div>
            </div>
            <div className="mt-2 space-y-0.5 text-xs text-slate-500 font-mono">
              {c.gstin && <p>GSTIN: {c.gstin}</p>}
              {c.pan && <p>PAN: {c.pan}</p>}
            </div>
          </div>
          <div className="text-right">
            <p className="font-display text-2xl font-extrabold tracking-tight">TAX INVOICE</p>
            <p className="font-mono text-sm font-semibold mt-1" data-testid="invoice-no">{inv.invoice_no}</p>
            <p className="text-xs text-slate-500 mt-1">Date: {fmtDate(inv.date)}{inv.due_date ? ` · Due: ${fmtDate(inv.due_date)}` : ""}</p>
            <div className="mt-2"><StatusBadge status={inv.payment_status} /></div>
          </div>
        </div>

        <Separator className="my-5" />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Billing Address</p>
            <p className="font-semibold">{inv.party_name}</p>
            <p className="text-xs text-slate-500">{addressText(inv.billing_address_details) || inv.billing_address || [p.billing_address, p.city, p.state, p.pin].filter(Boolean).join(", ")}</p>
            {inv.party_gstin && <p className="text-xs font-mono text-slate-500 mt-0.5">GSTIN: {inv.party_gstin}</p>}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Shipping Address</p>
            <p className="font-semibold">{inv.party_name}</p>
            <p className="text-xs text-slate-500">{addressText(inv.shipping_address_details) || inv.shipping_address || [p.shipping_address, p.city, p.state, p.pin].filter(Boolean).join(", ")}</p>
          </div>
          <div className="sm:text-right">
            <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Supply</p>
            <p className="text-sm">Place of Supply: <b>{inv.place_of_supply || "—"}</b></p>
            <p className="text-xs text-slate-500 capitalize">Invoice type: {inv.invoice_type}</p>
            <p className="text-xs text-slate-500">{inv.intra_state ? "Intra-state (CGST + SGST)" : "Inter-state (IGST)"}</p>
          </div>
        </div>

        <div className="mt-5 overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
          <table className="w-full text-sm min-w-[640px]" data-testid="invoice-items-table">
            <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              <th className="p-2.5">#</th><th className="p-2.5">Item</th><th className="p-2.5">HSN</th>
              <th className="p-2.5 text-right">Qty</th><th className="p-2.5 text-right">Rate</th>
              <th className="p-2.5 text-right">Taxable</th>
              {inv.intra_state ? (<><th className="p-2.5 text-right">CGST</th><th className="p-2.5 text-right">SGST</th></>) : (<th className="p-2.5 text-right">IGST</th>)}
              <th className="p-2.5 text-right">Total</th></tr></thead>
            <tbody>
              {inv.items.map((l, i) => (
                <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="p-2.5 text-slate-400">{i + 1}</td>
                  <td className="p-2.5 font-medium">{l.name}{l.discount_pct ? <span className="ml-1 text-xs text-emerald-600">(-{l.discount_pct}%)</span> : null}</td>
                  <td className="p-2.5 font-mono text-xs text-slate-500">{l.hsn || "—"}</td>
                  <td className="p-2.5 text-right font-mono">{l.qty} {l.unit}</td>
                  <td className="p-2.5 text-right font-mono">{inr(l.rate)}</td>
                  <td className="p-2.5 text-right font-mono">{inr(l.taxable)}</td>
                  {inv.intra_state ? (<>
                    <td className="p-2.5 text-right font-mono text-xs">{inr(l.cgst)}</td>
                    <td className="p-2.5 text-right font-mono text-xs">{inr(l.sgst)}</td>
                  </>) : (<td className="p-2.5 text-right font-mono text-xs">{inr(l.igst)}</td>)}
                  <td className="p-2.5 text-right font-mono font-semibold">{inr(l.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="text-xs text-slate-500 space-y-1">
            <p className="font-semibold text-slate-700 dark:text-slate-300">Amount in words</p>
            <p data-testid="amount-in-words" className="italic">{amountInWords(inv.total)}</p>
            {inv.notes && <><p className="font-semibold text-slate-700 dark:text-slate-300 mt-3">Notes</p><p>{inv.notes}</p></>}
            {(c.bank_name || c.upi) && (
              <><p className="font-semibold text-slate-700 dark:text-slate-300 mt-3">Payment Details</p>
              {c.bank_name && <p>{c.bank_name} · A/c {c.bank_account} · IFSC {c.bank_ifsc}</p>}
              {c.upi && <p>UPI: {c.upi}</p>}</>
            )}
          </div>
          <div className="space-y-1.5 text-sm">
            {[["Subtotal", inv.subtotal], ["Discount", -inv.discount], ["Taxable Amount", inv.taxable],
              ...(inv.intra_state ? [["CGST", inv.cgst], ["SGST", inv.sgst]] : [["IGST", inv.igst]]),
              ["Additional Charges", inv.additional_charges], ["Round Off", inv.round_off],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between text-slate-600 dark:text-slate-300">
                <span>{k}</span><span className="font-mono">{inr(v)}</span>
              </div>
            ))}
            <div className="flex justify-between border-t border-slate-200 dark:border-slate-700 pt-1.5 font-display font-bold">
              <span>Grand Total</span><span className="font-mono" data-testid="invoice-total">{inr(inv.total)}</span>
            </div>
            <div className="flex justify-between text-emerald-600 dark:text-emerald-400">
              <span>Paid</span><span className="font-mono" data-testid="invoice-paid">{inr(inv.paid)}</span>
            </div>
            <div className="flex justify-between font-semibold text-red-600 dark:text-red-400">
              <span>Balance Due</span><span className="font-mono" data-testid="invoice-balance">{inr(inv.balance)}</span>
            </div>
          </div>
        </div>

        {inv.payments?.length > 0 && (
          <div className="mt-6 no-print">
            <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Payment History</p>
            <div className="space-y-1.5">
              {inv.payments.map((pay) => (
                <div key={pay.id} className="flex justify-between rounded-lg bg-slate-50 dark:bg-slate-800/50 px-3 py-2 text-sm" data-testid={`payment-${pay.payment_no}`}>
                  <span className="font-mono text-xs">{pay.payment_no} · {fmtDate(pay.date)} · <span className="capitalize">{pay.mode?.replace(/_/g, " ")}</span></span>
                  <Money value={pay.allocations?.filter((a) => a.invoice_id === id).reduce((s, a) => s + a.amount, 0) || pay.amount} className="font-semibold text-emerald-600" />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 flex justify-between items-end text-xs text-slate-400">
          <p>Created by {inv.created_by_name || "—"} · This is a system-generated invoice.</p>
          <div className="text-center min-w-[150px]">
            {inv.signature_url ? (
              <img src={inv.signature_url} alt="Authorized Signature" className="mx-auto h-12 w-36 object-contain object-bottom" />
            ) : (
              <div className="h-12 w-36 border-b border-slate-300 dark:border-slate-600" />
            )}
            <p className="mt-1">Authorized Signatory</p>
          </div>
        </div>
      </div>

      <Dialog open={cnOpen} onOpenChange={setCnOpen}>
        <DialogContent aria-describedby={undefined} className="max-w-lg bg-white dark:bg-slate-900" data-testid="credit-note-dialog">
          <DialogHeader><DialogTitle className="font-display">Credit Note — {inv.invoice_no}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Returned quantities restock automatically and reduce the customer's balance.</p>
          <div className="max-h-60 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-800">
            {inv.items.map((l) => (
              <div key={l.name} className="flex items-center gap-3 border-b border-slate-100 dark:border-slate-800 px-3 py-2 text-sm">
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{l.name}</p>
                  <p className="text-xs text-slate-500">invoiced {l.qty} {l.unit}</p>
                </div>
                <Input data-testid={`cn-qty-${l.name}`} type="number" min="0" max={l.qty} className="h-8 w-24 font-mono text-xs"
                  value={cnQtys[l.name] || ""} placeholder="0"
                  onChange={(e) => setCnQtys((q) => ({ ...q, [l.name]: Math.min(+e.target.value || 0, l.qty) }))} />
              </div>
            ))}
          </div>
          <div>
            <Label>Reason *</Label>
            <Input data-testid="cn-reason" className="mt-1.5" value={cnReason} onChange={(e) => setCnReason(e.target.value)} placeholder="Damaged goods, wrong item, price correction..." />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCnOpen(false)}>Cancel</Button>
            <Button data-testid="cn-save-btn" onClick={createCreditNote} disabled={cnBusy}
              className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900">{cnBusy ? "Posting..." : "Post Credit Note"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
